import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

export const THEME_STORAGE_KEY = 'market_tool_theme';

export const THEMES = {
  light: {
    bg: '#f0f4ff',
    card: '#ffffff',
    border: '#e2e8f0',
    borderLight: '#dcfce7',
    text: '#0f172a',
    textMuted: '#64748b',
    textMuted2: '#64748b',
    primary: '#166534',
    primaryLight: '#dcfce7',
    primaryMuted: '#86efac',
    primaryContrast: '#ffffff',
    accent: '#15803d',
    accentLight: '#bbf7d0',
    accentText: '#14532d',
    usageOk: '#15803d',
    usageWarn: '#d97706',
    usageError: '#dc2626',
    warning: '#f59e0b',
    errorBg: '#fee2e2',
    errorBorder: '#fecaca',
    errorText: '#991b1b',
    tabBarBg: '#f0f4ff',
    tabActiveBg: '#ffffff',
    tabActiveBorder: '#166534',
    tabInactiveText: '#64748b',
    inputBorder: '#cbd5e1',
    inputBg: '#ffffff',
    focusRing: 'rgba(22, 101, 52, 0.25)',
    shadow: '0 1px 3px rgba(0,0,0,0.08)',
    hoverBg: '#dcfce7',
    blue: '#1e40af',
    blueLight: '#dbeafe',
    blueMuted: '#93c5fd',
  },
  dark: {
    bg: '#0c1929',
    card: '#1e3a5f',
    border: '#334155',
    borderLight: '#475569',
    text: '#f0fdf4',
    textMuted: '#94a3b8',
    textMuted2: '#94a3b8',
    primary: '#4ade80',
    primaryLight: '#14532d',
    primaryMuted: '#166534',
    primaryContrast: '#0f172a',
    accent: '#86efac',
    accentLight: '#052e16',
    accentText: '#bbf7d0',
    usageOk: '#4ade80',
    usageWarn: '#fbbf24',
    usageError: '#f87171',
    warning: '#fbbf24',
    errorBg: '#7f1d1d',
    errorBorder: '#991b1b',
    errorText: '#fecaca',
    tabBarBg: '#0c1929',
    tabActiveBg: '#1e3a5f',
    tabActiveBorder: '#4ade80',
    tabInactiveText: '#94a3b8',
    inputBorder: '#475569',
    inputBg: '#1e3a5f',
    focusRing: 'rgba(74, 222, 128, 0.35)',
    shadow: '0 1px 3px rgba(0,0,0,0.3)',
    hoverBg: '#1e3a8a',
    blue: '#60a5fa',
    blueLight: '#1e3a8a',
    blueMuted: '#1e40af',
  },
};

const ThemeContext = createContext(null);

export function ThemeProvider({ children }) {
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window === 'undefined') return false;
    try {
      return window.localStorage.getItem(THEME_STORAGE_KEY) === 'dark';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, darkMode ? 'dark' : 'light');
    } catch (_) {}
  }, [darkMode]);

  const toggleTheme = useCallback(() => {
    setDarkMode((prev) => !prev);
  }, []);

  const theme = THEMES[darkMode ? 'dark' : 'light'];

  return (
    <ThemeContext.Provider value={{ theme, darkMode, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    return {
      theme: THEMES.light,
      darkMode: false,
      toggleTheme: () => {},
    };
  }
  return ctx;
}
